import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Progress } from '../components/ui/progress';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  PieChart, 
  TrendingUp, 
  TrendingDown,
  DollarSign, 
  Home,
  Zap,
  Car,
  ShoppingCart,
  Gamepad2,
  Heart,
  Plus,
  Trash2,
  Calculator,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { PieChart as RechartsPieChart, Cell, ResponsiveContainer, Pie } from 'recharts';

export const ExpenseMonitorPage = () => {
  const { t, i18n } = useTranslation();
  const [expenses, setExpenses] = useState({});
  const [totalIncome, setTotalIncome] = useState(0);
  const [newCategory, setNewCategory] = useState('');
  const [newAmount, setNewAmount] = useState('');

  const defaultCategories = [
    {
      key: 'housing',
      name: i18n.language === 'es' ? 'Vivienda' : 'Logement',
      icon: Home,
      color: '#4a8070',
      maxRecommended: 35
    },
    {
      key: 'energy',
      name: i18n.language === 'es' ? 'Energía' : 'Énergie',
      icon: Zap,
      color: '#f59e0b',
      maxRecommended: 8
    },
    {
      key: 'transport',
      name: i18n.language === 'es' ? 'Transporte' : 'Transport',
      icon: Car,
      color: '#3b82f6',
      maxRecommended: 15
    },
    {
      key: 'food',
      name: i18n.language === 'es' ? 'Alimentación' : 'Alimentation',
      icon: ShoppingCart,
      color: '#10b981',
      maxRecommended: 20
    },
    {
      key: 'leisure',
      name: i18n.language === 'es' ? 'Ocio' : 'Loisirs',
      icon: Gamepad2,
      color: '#8b5cf6',
      maxRecommended: 10
    },
    {
      key: 'health',
      name: i18n.language === 'es' ? 'Salud' : 'Santé',
      icon: Heart,
      color: '#ef4444',
      maxRecommended: 5
    }
  ];

  useEffect(() => {
    // Load saved data or set defaults
    const saved = localStorage.getItem('savea-expense-monitor');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        setExpenses(data.expenses || {});
        setTotalIncome(data.totalIncome || 3000);
      } catch (error) {
        console.error('Error loading expenses:', error);
        setTotalIncome(3000); // Default 3000€
      }
    } else {
      // Set default values for new users
      setTotalIncome(3000);
      setExpenses({
        housing: 1050, // 35% of 3000
        energy: 240,   // 8% of 3000
        food: 600,     // 20% of 3000
        transport: 450, // 15% of 3000
        leisure: 300,  // 10% of 3000
        health: 150    // 5% of 3000
      });
    }
  }, []);

  useEffect(() => {
    // Save data
    const data = {
      expenses,
      totalIncome
    };
    localStorage.setItem('savea-expense-monitor', JSON.stringify(data));
  }, [expenses, totalIncome]);

  const updateExpense = (categoryKey, amount) => {
    setExpenses(prev => ({
      ...prev,
      [categoryKey]: parseFloat(amount) || 0
    }));
  };

  const addCustomCategory = () => {
    if (!newCategory.trim() || !newAmount) return;
    
    const customKey = `custom_${Date.now()}`;
    setExpenses(prev => ({
      ...prev,
      [customKey]: parseFloat(newAmount)
    }));
    
    // Add to categories list
    defaultCategories.push({
      key: customKey,
      name: newCategory.trim(),
      icon: Plus,
      color: '#6b7280',
      maxRecommended: 10,
      isCustom: true
    });
    
    setNewCategory('');
    setNewAmount('');
    toast.success(i18n.language === 'es' ? 'Categoría añadida' : 'Catégorie ajoutée');
  };

  const deleteCustomCategory = (categoryKey) => {
    setExpenses(prev => {
      const newExpenses = { ...prev };
      delete newExpenses[categoryKey];
      return newExpenses;
    });
    toast.success(i18n.language === 'es' ? 'Categoría eliminada' : 'Catégorie supprimée');
  };

  const getTotalExpenses = () => {
    return Object.values(expenses).reduce((total, amount) => total + amount, 0);
  };

  const getRemainingIncome = () => {
    return totalIncome - getTotalExpenses();
  };

  const getSavingsRate = () => {
    if (totalIncome === 0) return 0;
    return ((getRemainingIncome() / totalIncome) * 100);
  };

  const getCategoryPercentage = (amount) => {
    if (totalIncome === 0) return 0;
    return ((amount / totalIncome) * 100);
  };

  const getStatusIcon = (categoryKey, percentage, maxRecommended) => {
    if (percentage <= maxRecommended) {
      return <CheckCircle className="w-4 h-4 text-green-500" />;
    }
    return <AlertTriangle className="w-4 h-4 text-red-500" />;
  };

  const pieData = defaultCategories
    .filter(cat => expenses[cat.key] > 0)
    .map(category => ({
      name: category.name,
      value: expenses[category.key] || 0,
      color: category.color,
      percentage: getCategoryPercentage(expenses[category.key] || 0)
    }));

  const monthlyData = [
    { month: i18n.language === 'es' ? 'Sep' : 'Sep', income: totalIncome, expenses: getTotalExpenses(), savings: getRemainingIncome() },
    { month: i18n.language === 'es' ? 'Oct' : 'Oct', income: totalIncome, expenses: getTotalExpenses() * 0.95, savings: totalIncome - (getTotalExpenses() * 0.95) },
    { month: i18n.language === 'es' ? 'Nov' : 'Nov', income: totalIncome, expenses: getTotalExpenses() * 1.1, savings: totalIncome - (getTotalExpenses() * 1.1) }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white">
      {/* Hero Section */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 
              className="text-4xl lg:text-5xl font-bold text-sage-900 mb-4"
              data-testid="expense-monitor-title"
            >
              {t('tools.expenses.title')}
            </h1>
            <p className="text-xl text-sage-600 max-w-3xl mx-auto">
              {i18n.language === 'es'
                ? 'Analiza tus gastos mensuales y optimiza tu presupuesto familiar con recomendaciones personalizadas.'
                : 'Analysez vos dépenses mensuelles et optimisez votre budget familial avec des recommandations personnalisées.'
              }
            </p>
          </div>
        </div>
      </section>

      {/* Income Section */}
      <section className="pb-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="mb-8">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                {i18n.language === 'es' ? 'Ingresos Mensuales' : 'Revenus Mensuels'}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label htmlFor="income">
                    {i18n.language === 'es' ? 'Ingresos totales netos (€/mes)' : 'Revenus totaux nets (€/mois)'}
                  </Label>
                  <Input
                    id="income"
                    type="number"
                    placeholder="3000"
                    value={totalIncome || ''}
                    onChange={(e) => setTotalIncome(parseFloat(e.target.value) || 0)}
                    className="text-lg font-medium border-sage-200 focus:border-sage-500"
                  />
                </div>
                
                {totalIncome > 0 && (
                  <div className="text-right">
                    <div className="text-2xl font-bold text-green-600">{totalIncome}€</div>
                    <div className="text-sm text-sage-600">
                      {i18n.language === 'es' ? 'Por mes' : 'Par mois'}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Dashboard */}
      {totalIncome > 0 && (
        <>
          {/* Summary Cards */}
          <section className="pb-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
                <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-green-700">{getRemainingIncome().toFixed(0)}€</div>
                        <div className="text-sm text-green-600">
                          {i18n.language === 'es' ? 'Restante' : 'Restant'}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                        <Calculator className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-blue-700">{getTotalExpenses().toFixed(0)}€</div>
                        <div className="text-sm text-blue-600">
                          {i18n.language === 'es' ? 'Gastos totales' : 'Dépenses totales'}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gradient-to-br from-purple-50 to-pink-50 border-purple-200">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                        <PieChart className="w-6 h-6 text-white" />
                      </div>
                      <div>
                        <div className="text-2xl font-bold text-purple-700">{getSavingsRate().toFixed(1)}%</div>
                        <div className="text-sm text-purple-600">
                          {i18n.language === 'es' ? 'Tasa de ahorro' : 'Taux d\'épargne'}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card className={`bg-gradient-to-br ${getSavingsRate() >= 20 ? 'from-green-50 to-emerald-50 border-green-200' : getSavingsRate() >= 10 ? 'from-orange-50 to-yellow-50 border-orange-200' : 'from-red-50 to-pink-50 border-red-200'}`}>
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${getSavingsRate() >= 20 ? 'bg-green-500' : getSavingsRate() >= 10 ? 'bg-orange-500' : 'bg-red-500'}`}>
                        {getSavingsRate() >= 20 ? <CheckCircle className="w-6 h-6 text-white" /> : <AlertTriangle className="w-6 h-6 text-white" />}
                      </div>
                      <div>
                        <div className={`text-lg font-bold ${getSavingsRate() >= 20 ? 'text-green-700' : getSavingsRate() >= 10 ? 'text-orange-700' : 'text-red-700'}`}>
                          {getSavingsRate() >= 20 ? (i18n.language === 'es' ? 'Excelente' : 'Excellent') : 
                           getSavingsRate() >= 10 ? (i18n.language === 'es' ? 'Bien' : 'Bien') : 
                           (i18n.language === 'es' ? 'Mejorable' : 'À améliorer')}
                        </div>
                        <div className={`text-sm ${getSavingsRate() >= 20 ? 'text-green-600' : getSavingsRate() >= 10 ? 'text-orange-600' : 'text-red-600'}`}>
                          {i18n.language === 'es' ? 'Salud financiera' : 'Santé financière'}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </section>

          {/* Input Section */}
          <section className="pb-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Expense Input */}
                <Card>
                  <CardHeader>
                    <CardTitle>
                      {i18n.language === 'es' ? 'Ingresa tus gastos mensuales' : 'Saisissez vos dépenses mensuelles'}
                    </CardTitle>
                    <CardDescription>
                      {i18n.language === 'es' 
                        ? 'Completa cada categoría con tus gastos reales para obtener un análisis preciso'
                        : 'Complétez chaque catégorie avec vos dépenses réelles pour obtenir une analyse précise'
                      }
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {defaultCategories.map((category) => {
                      const IconComponent = category.icon;
                      const currentAmount = expenses[category.key] || 0;
                      const percentage = getCategoryPercentage(currentAmount);
                      const isOverBudget = percentage > category.maxRecommended;

                      return (
                        <div key={category.key} className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="flex items-center gap-2">
                              <IconComponent className="w-4 h-4" style={{ color: category.color }} />
                              {category.name}
                            </Label>
                            <div className="text-sm text-sage-600">
                              {percentage.toFixed(1)}% ({i18n.language === 'es' ? 'rec.' : 'rec.'} ≤{category.maxRecommended}%)
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Input
                              type="number"
                              placeholder="0"
                              value={expenses[category.key] || ''}
                              onChange={(e) => updateExpense(category.key, e.target.value)}
                              className={`flex-1 ${isOverBudget ? 'border-red-300 bg-red-50' : 'border-sage-200'}`}
                            />
                            <div className="text-sm font-medium text-sage-700 min-w-[40px]">€</div>
                            {getStatusIcon(category.key, percentage, category.maxRecommended)}
                          </div>
                          {isOverBudget && (
                            <div className="text-xs text-red-600 flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              {i18n.language === 'es' 
                                ? `Excede el presupuesto recomendado en ${(percentage - category.maxRecommended).toFixed(1)}%`
                                : `Dépasse le budget recommandé de ${(percentage - category.maxRecommended).toFixed(1)}%`
                              }
                            </div>
                          )}
                        </div>
                      );
                    })}

                    <Separator />

                    {/* Custom Category */}
                    <div className="space-y-3">
                      <Label>
                        {i18n.language === 'es' ? 'Añadir categoría personalizada' : 'Ajouter catégorie personnalisée'}
                      </Label>
                      <div className="grid grid-cols-3 gap-2">
                        <Input
                          placeholder={i18n.language === 'es' ? 'Nombre...' : 'Nom...'}
                          value={newCategory}
                          onChange={(e) => setNewCategory(e.target.value)}
                        />
                        <Input
                          type="number"
                          placeholder={i18n.language === 'es' ? 'Importe...' : 'Montant...'}
                          value={newAmount}
                          onChange={(e) => setNewAmount(e.target.value)}
                        />
                        <Button 
                          onClick={addCustomCategory}
                          disabled={!newCategory.trim() || !newAmount}
                          size="sm"
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Charts */}
                <div className="space-y-6">
                  {/* Pie Chart */}
                  {pieData.length > 0 && (
                    <Card>
                      <CardHeader>
                        <CardTitle className="text-center">
                          {i18n.language === 'es' ? 'Distribución de Gastos' : 'Répartition des Dépenses'}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ResponsiveContainer width="100%" height={300}>
                          <RechartsPieChart>
                            <Pie
                              data={pieData}
                              cx="50%"
                              cy="50%"
                              outerRadius={80}
                              dataKey="value"
                              label={({name, percentage}) => `${name}: ${percentage.toFixed(1)}%`}
                            >
                              {pieData.map((entry, index) => (
                                <Cell key={index} fill={entry.color} />
                              ))}
                            </Pie>
                          </RechartsPieChart>
                        </ResponsiveContainer>
                        
                        <div className="mt-4 space-y-2">
                          {pieData.map((data, index) => (
                            <div key={index} className="flex items-center justify-between text-sm">
                              <div className="flex items-center gap-2">
                                <div 
                                  className="w-3 h-3 rounded-full"
                                  style={{ backgroundColor: data.color }}
                                />
                                <span>{data.name}</span>
                              </div>
                              <span className="font-medium">{data.value}€ ({data.percentage.toFixed(1)}%)</span>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  )}

                  {/* Savings Goal */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <TrendingUp className="w-5 h-5 text-green-600" />
                        {i18n.language === 'es' ? 'Objetivo de Ahorro' : 'Objectif d\'Épargne'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-center mb-4">
                        <div className="text-3xl font-bold text-green-600 mb-1">
                          {getSavingsRate().toFixed(1)}%
                        </div>
                        <div className="text-sm text-sage-600">
                          {i18n.language === 'es' ? 'Tasa de ahorro actual' : 'Taux d\'épargne actuel'}
                        </div>
                      </div>
                      
                      <Progress value={getSavingsRate()} className="mb-4" />
                      
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>{i18n.language === 'es' ? 'Objetivo mínimo:' : 'Objectif minimum :'}</span>
                          <span className="font-medium text-orange-600">10%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>{i18n.language === 'es' ? 'Objetivo ideal:' : 'Objectif idéal :'}</span>
                          <span className="font-medium text-green-600">20%</span>
                        </div>
                        <div className="flex justify-between font-medium">
                          <span>{i18n.language === 'es' ? 'Tu situación:' : 'Votre situation :'}</span>
                          <span className={getSavingsRate() >= 20 ? 'text-green-600' : getSavingsRate() >= 10 ? 'text-orange-600' : 'text-red-600'}>
                            {getRemainingIncome().toFixed(0)}€ ({getSavingsRate().toFixed(1)}%)
                          </span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>
          </section>

          {/* Recommendations */}
          {getSavingsRate() < 20 && (
            <section className="pb-20">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <Card className="bg-gradient-to-r from-amber-50 to-orange-50 border-amber-200">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-amber-800">
                      <AlertTriangle className="w-5 h-5" />
                      {i18n.language === 'es' ? 'Recomendaciones de Optimización' : 'Recommandations d\'Optimisation'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h4 className="font-medium text-amber-800 mb-3">
                          {i18n.language === 'es' ? 'Categorías a revisar:' : 'Catégories à réviser :'}
                        </h4>
                        <div className="space-y-2">
                          {defaultCategories
                            .filter(cat => getCategoryPercentage(expenses[cat.key] || 0) > cat.maxRecommended)
                            .map(cat => (
                              <div key={cat.key} className="flex items-center justify-between text-sm">
                                <span className="flex items-center gap-2">
                                  <cat.icon className="w-4 h-4" />
                                  {cat.name}
                                </span>
                                <span className="text-red-600 font-medium">
                                  -{(getCategoryPercentage(expenses[cat.key] || 0) - cat.maxRecommended).toFixed(1)}% {i18n.language === 'es' ? 'a reducir' : 'à réduire'}
                                </span>
                              </div>
                            ))}
                        </div>
                      </div>
                      
                      <div>
                        <h4 className="font-medium text-amber-800 mb-3">
                          {i18n.language === 'es' ? 'Acciones sugeridas:' : 'Actions suggérées :'}
                        </h4>
                        <ul className="space-y-2 text-sm text-amber-700">
                          <li>• {i18n.language === 'es' ? 'Usa nuestros comparadores de proveedores' : 'Utilisez nos comparateurs de fournisseurs'}</li>
                          <li>• {i18n.language === 'es' ? 'Consulta nuestras plantillas de negociación' : 'Consultez nos templates de négociation'}</li>
                          <li>• {i18n.language === 'es' ? 'Lee nuestros e-books de ahorro' : 'Lisez nos e-books d\'économie'}</li>
                          <li>• {i18n.language === 'es' ? 'Revisa suscripciones innecesarias' : 'Révisez abonnements inutiles'}</li>
                        </ul>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </section>
          )}
        </>
      )}
    </div>
  );
};